import { useEffect, useMemo, useState } from "react"
import Modal from "Components/molecule/modal"
import TextInput from "Components/molecule/inputs/text-input"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import { LambdaRunKind, LambdaRunOptions, LambdaTrackerUI } from "models/lambda-tracker"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

interface LambdaRunDialogProps {
    isOpen: boolean
    onClose: () => void
    /** One Lambda (Run) or every Lambda (Update all). */
    target: { mode: "single", name: string, kind: LambdaRunKind } | { mode: "all", count: number }
    onSubmit: (options: LambdaRunOptions) => Promise<boolean>
}

const MAX_BACKFILL_YEARS = 3
const isoToday = () => new Date().toISOString().slice(0, 10)
const isoYearsAgo = (years: number) => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - years)
    return d.toISOString().slice(0, 10)
}

/** Fiscal years touched by [syncFrom, today]; the current one with no date. Mirrors the API. */
const fiscalYearsFor = (syncFrom: string): number[] => {
    const last = FiscalCalendar.currentFiscalYear()
    const first = syncFrom ? calendar.getFiscalYear(new Date(`${syncFrom}T00:00:00Z`)) : last
    const years: number[] = []
    for (let y = first; y <= last; y++) years.push(y)
    return years
}

const monthsBetween = (syncFrom: string): number => {
    const from = new Date(`${syncFrom}T00:00:00Z`)
    const now = new Date()
    return (now.getUTCFullYear() - from.getUTCFullYear()) * 12 + now.getUTCMonth() - from.getUTCMonth() + 1
}

// Asks for the run options: a "since" date for incremental syncs (backfill) and fiscal years for reports.
const LambdaRunDialog = ({ isOpen, onClose, target, onSubmit }: LambdaRunDialogProps) => {
    const [syncFrom, setSyncFrom] = useState<string>("")
    const [years, setYears] = useState<number[]>([])
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const kind = target.mode === "single" ? target.kind : null
    const usesDate = target.mode === "all" || kind === "incremental"
    const usesYears = kind === "fiscalYear" || kind === "fiscalYears" || kind === "budgetYears"
    const currentFy = FiscalCalendar.currentFiscalYear()
    const yearOptions = [currentFy - 3, currentFy - 2, currentFy - 1, currentFy]

    useEffect(() => {
        if (isOpen) {
            setSyncFrom("")
            setYears([currentFy])
        }
    }, [isOpen])

    const dateError = syncFrom && (syncFrom > isoToday() || syncFrom < isoYearsAgo(MAX_BACKFILL_YEARS))
        ? `Choose a date between ${isoYearsAgo(MAX_BACKFILL_YEARS)} and today.`
        : null

    const summary = useMemo(() => {
        if (target.mode === "all") {
            const fy = fiscalYearsFor(syncFrom).map(y => `FY${y}`).join(", ")
            return syncFrom
                ? `${target.count} Lambdas. Changes since ${syncFrom} are synced month by month (${monthsBetween(syncFrom)} runs per incremental Lambda), full syncs run once, then the reports are rebuilt for ${fy}.`
                : `${target.count} Lambdas with their usual window (last 3 days), then the reports are rebuilt for ${fy}.`
        }
        if (kind === "incremental") {
            return syncFrom ? `${monthsBetween(syncFrom)} runs, one per month, from ${syncFrom} to now.` : "One run covering the last 3 days."
        }
        if (usesYears) return years.length ? `Fiscal years: ${years.map(y => `FY${y}`).join(", ")}.` : "Choose at least one fiscal year."
        return "One run."
    }, [target, kind, syncFrom, years, usesYears])

    const toggleYear = (y: number) =>
        setYears(prev => prev.includes(y) ? prev.filter(v => v !== y) : [...prev, y].sort((a, b) => a - b))

    const submit = async () => {
        setIsSubmitting(true)
        const ok = await onSubmit({
            syncFrom: usesDate && syncFrom ? syncFrom : undefined,
            fiscalYears: usesYears ? years : undefined,
        })
        setIsSubmitting(false)
        if (ok) onClose()
    }

    const title = target.mode === "all" ? "Update all" : `Run ${target.name}`

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}
            subtitle={target.mode === "all" ? "Source syncs (Acumatica, Dayforce) first, then the IbexIQ reports. Email jobs are not included." : (kind ? LambdaTrackerUI.runKindHelp(kind) : undefined)}>
            <div className="lambda-run-dialog d-flex flex-column gap-3">
                {usesDate && (
                    <TextInput
                        label="Sync changes since (optional)"
                        type="date"
                        min={isoYearsAgo(MAX_BACKFILL_YEARS)}
                        max={isoToday()}
                        value={syncFrom}
                        onChange={e => setSyncFrom(e.target.value)}
                        error={!!dateError}
                        message={dateError ?? "Use it to catch up after the Lambdas were down. Empty = the usual last 3 days."}
                    />
                )}

                {usesYears && (
                    <div className="input-container">
                        <span className="input-label">Fiscal years</span>
                        <div className="d-flex flex-wrap gap-2">
                            {yearOptions.map(y => (
                                <button key={y} type="button"
                                    className={`btn btn-sm ${years.includes(y) ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => toggleYear(y)}
                                    aria-pressed={years.includes(y)}>
                                    FY{y}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="alert alert-info mb-0 py-2">
                    <i className="mdi mdi-information-outline me-1" aria-hidden />{summary}
                    <div><small>Runs one after another and can take several minutes. Follow them in the <strong>Manual runs</strong> tab.</small></div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                    <PrimaryButton onClick={submit} disabled={isSubmitting || !!dateError || (usesYears && years.length === 0)}>
                        {isSubmitting ? "Starting…" : target.mode === "all" ? "Start Update all" : "Run now"}
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    )
}

export default LambdaRunDialog
